import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

// ─── S3 Upload and Types ─────────────────────────────────────────────
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";

// ─── UI and Styling ──────────────────────────────────────────────────
import styles from "./styles.module.scss";
import classNames from "classnames";

// ─── i18n & Components ───────────────────────────────────────────────
import { useTranslation } from "react-i18next";
import PDFComponent from "../pdfComponent";
import {
  handleCompressImage,
  MAX_FILE_SIZE,
} from "../../../setup/util/helpers";
import ImageUpload from "../imageUpload";

// ─── Types ───────────────────────────────────────────────────────────
interface UploadEntity {
  file: File;
  s3File: string;
  isLoading: boolean;
  loadingProgress: number;
  previewUrl?: string;
}

interface Props {
  errorMessage?: string;
  maxFiles?: number;
  initialFiles?: string[];
  onFilesChange: (files: string[]) => void;
  onUploadStatusChange?: (isUploading: boolean) => void;
}

export interface FileUploadManagerRef {
  clearAllFiles: () => void;
  isUploading: () => boolean;
}

// ─── Component ───────────────────────────────────────────────────────
const FileUploadManager = forwardRef<FileUploadManagerRef, Props>(
  (
    {
      maxFiles = 10,
      initialFiles = [],
      onFilesChange,
      errorMessage,
      onUploadStatusChange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [entities, setEntities] = useState<UploadEntity[]>([]);
    const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>(
      {}
    );
    const [error, setError] = useState<string | null>(null);
    const initialized = useRef(false);

    useEffect(() => {
      setError(errorMessage || "");
    }, [errorMessage]);

    // ─── Clear All Files ────────────────────────────────────────────
    const clearAllFiles = () => {
      entities.forEach(
        (e) => e.previewUrl && URL.revokeObjectURL(e.previewUrl)
      );
      setEntities([]);
      onFilesChange([]);
      initialized.current = false;
    };

    useImperativeHandle(ref, () => ({
      clearAllFiles,
      isUploading: () => entities.some((e) => e.isLoading),
    }));

    // ─── Load Initial Files (S3 URLs) ───────────────────────────────
    useEffect(() => {
      if (!initialized.current && initialFiles.length > 0) {
        const mapped = initialFiles.map((url) => ({
          file: {} as File,
          s3File: url,
          isLoading: false,
          loadingProgress: 0,
        }));
        setEntities(mapped);
        initialized.current = true;
      }
    }, [initialFiles]);

    // ─── Notify Parent on Upload Completion ─────────────────────────
    useEffect(() => {
      const isCurrentlyUploading = entities.some((e) => e.isLoading);
      onUploadStatusChange?.(isCurrentlyUploading);

      const allDone = !isCurrentlyUploading;
      if (allDone) {
        onFilesChange(entities.map((e) => e.s3File).filter(Boolean));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entities]);

    // ─── Handle Upload ──────────────────────────────────────────────
    const handleUpload = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(null); // reset previous error

        const selectedFiles = Array.from(e.target.files || []).slice(
          0,
          maxFiles - entities.length
        );

        const allowedFormats = [
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/webp",
          "application/pdf",
        ];

        const allowed = selectedFiles.filter((file) =>
          allowedFormats.includes(file.type)
        );

        const oversizeFile = allowed.find((file) => {
          console.log("file", file);
          console.log("MAX_FILE_SIZE", MAX_FILE_SIZE);
          return file.size > MAX_FILE_SIZE;
        });
        // todo implement snackbar for this implementation

        const tempEntities: UploadEntity[] = [];

        for (const file of allowed) {
          let uploadFileTarget = file;

          if (file.type !== "application/pdf") {
            try {
              const compressed = await handleCompressImage(file);
              if (compressed) {
                uploadFileTarget = compressed;
              }
            } catch (error) {
              console.error("Image compression failed", error);
            }
          }

          const placeholder: UploadEntity = {
            file: uploadFileTarget,
            s3File: "",
            isLoading: true,
            loadingProgress: 0,
            previewUrl: URL.createObjectURL(uploadFileTarget),
          };

          tempEntities.push(placeholder);
        }

        setEntities((prev) => [...prev, ...tempEntities]);

        for (const entity of tempEntities) {
          try {
            const url = await uploadFile(
              entity.file,
              EUploadImageType.CONTRACT,
              "STEP_ONE_UPLOAD",
              (progress) => {
                setEntities((prev) =>
                  prev.map((e) =>
                    e.file.name === entity.file.name
                      ? {
                          ...e,
                          loadingProgress: Math.round(
                            (progress.loaded / progress.total) * 100
                          ),
                        }
                      : e
                  )
                );
              }
            );

            setEntities((prev) =>
              prev.map((e) =>
                e.file.name === entity.file.name
                  ? {
                      ...e,
                      s3File: url,
                      isLoading: false,
                      loadingProgress: 0,
                    }
                  : e
              )
            );
          } catch (err) {
            console.error("Upload failed", err);
          }
        }
      },
      [entities.length, maxFiles, t]
    );

    // ─── Handle Deletion with Cleanup ───────────────────────────────
    const handleDelete = useCallback((index: number) => {
      setEntities((prev) => {
        const toRemove = prev[index];
        if (toRemove?.previewUrl) {
          URL.revokeObjectURL(toRemove.previewUrl);
        }
        return prev.filter((_, i) => i !== index);
      });
    }, []);

    // ─── Render ─────────────────────────────────────────────────────
    return (
      <div className={styles.container}>
        <h3>{t("creation_contract_details_attachment_title")}</h3>
        <p className={styles.para}>
          {t("creation_contract_details_attachment_subtitle")}
        </p>

        <div className={styles.images_upload_section}>
          <ImageUpload
            multiple
            withPDF
            disabled={entities.length >= maxFiles}
            handleChange={
              entities.length < maxFiles
                ? handleUpload
                : () => {
                    console.log("File changed");
                  }
            }
          />
          {entities.map((entity, index) => (
            <div
              key={entity.s3File || entity.previewUrl || index}
              className={styles["uploaded-container"]}>
              {entity.isLoading ? (
                <div className={styles["upload-wrapper"]}>
                  {entity.file.type?.startsWith("image/") &&
                  entity.previewUrl ? (
                    <img
                      src={entity.previewUrl}
                      alt="preview"
                      className={styles["local-image"]}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAE0lEQVR4nO3BMQEAAAgDoJvcv+MHF4AAAAAAAPgPXj8AAQsTxnsAAAAASUVORK5CYII=";
                      }}
                    />
                  ) : (
                    <PDFComponent fileUrl="" isPreview />
                  )}

                  <div
                    style={{ height: `${100 - entity.loadingProgress}%` }}
                    className={styles["upload-overlay"]}
                  />
                  <div className={styles["upload-overlay_text"]}>
                    <span>{entity.loadingProgress}%</span>
                  </div>
                </div>
              ) : entity.s3File?.endsWith(".pdf") ? (
                <PDFComponent fileUrl={entity.s3File} isPreview />
              ) : (
                <img
                  className={classNames(styles["uploaded-image"], {
                    [styles["loaded"]]: loadedImages[index],
                  })}
                  src={entity.s3File}
                  alt="Uploaded"
                  onLoad={() =>
                    setLoadedImages((prev) => ({ ...prev, [index]: true }))
                  }
                />
              )}

              <div onClick={() => handleDelete(index)}>
                <img
                  className={styles["delete-icon"]}
                  src="/assets/images/svg/delete_org.svg"
                  alt="delete"
                />
              </div>
            </div>
          ))}
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    );
  }
);

FileUploadManager.displayName = "FileUploadManager";
export default FileUploadManager;
